import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Play, Trophy, Zap, Dumbbell, Target, Feather, Waves, BedDouble, Cpu, Landmark, Grid3x3, Goal, Warehouse, HeartHandshake, Clock, Infinity as InfinityIcon, Sparkles, BadgeCheck, X } from "lucide-react";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { useHubMember } from "@/lib/HubMemberContext";

const ICONS = { Barn: Warehouse, Target, Cpu, Feather, Landmark, Grid3x3, Dumbbell, Waves, Trophy, Goal, BedDouble };

export default function Home() {
  const { member: hubMember } = useHubMember();
  const [joinBannerDismissed, setJoinBannerDismissed] = useState(() => {
    try { return sessionStorage.getItem("hide_join_banner") === "1"; } catch { return false; }
  });
  const showJoinBanner = !hubMember && !joinBannerDismissed;
  const dismissBanner = () => {
    try { sessionStorage.setItem("hide_join_banner", "1"); } catch { /* noop */ }
    setJoinBannerDismissed(true);
  };
  const [copy, setCopy] = useState({});
  const [services, setServices] = useState([]);
  const [news, setNews] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [slideIdx, setSlideIdx] = useState(0);
  const [ambientVideo, setAmbientVideo] = useState(null);
  const [calcSlug, setCalcSlug] = useState("indoor-box");
  const [calcHours, setCalcHours] = useState(2);
  const [donationsOn, setDonationsOn] = useState(false);
  const [org, setOrg] = useState({ company_type: "company", gst_rate: 18, gst_inclusive: false });

  useEffect(() => {
    api.get("/homepage").then((r) => setCopy(r.data));
    api.get("/services").then((r) => setServices(r.data));
    api.get("/news").then((r) => setNews(r.data));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
    api.get("/blogs").then((r) => setBlogs(r.data)).catch(() => setBlogs([]));
    api.get("/public/membership-plans").then((r) => setMembershipPlans(r.data || [])).catch(() => setMembershipPlans([]));
    api.get("/payment-status").then((r) => setDonationsOn(!!r.data?.donations_enabled)).catch(() => {});
    api.get("/public/ngo").then((r) => setOrg(r.data || {})).catch(() => {});
    api.get("/gallery").then((r) => {
      const items = r.data || [];
      setGallery(items);
      const videos = items.filter((it) => it.media_type === "video" && it.video_url);
      if (videos.length) {
        setAmbientVideo(videos[Math.floor(Math.random() * videos.length)]);
      }
    });
  }, []);

  // Auto-advance the slideshow every 4.5s
  useEffect(() => {
    const images = gallery.filter((g) => g.media_type !== "video");
    if (images.length < 2) return;
    const t = setInterval(() => setSlideIdx((i) => (i + 1) % images.length), 4500);
    return () => clearInterval(t);
  }, [gallery]);

  const slideshowImages = gallery.filter((g) => g.media_type !== "video");

  const hourlyServices = services.filter((s) => s.type === "hourly");
  const selectedSvc = hourlyServices.find((s) => s.slug === calcSlug) || hourlyServices[0];
  const calcSubtotal = selectedSvc ? (selectedSvc.effective_hourly_rate || selectedSvc.unit_price || 0) * calcHours : 0;
  const calcTotal = calcSubtotal;

  return (
    <div data-testid="home-page">
      {/* JOIN BANNER — slim strip for first-time visitors */}
      {showJoinBanner && (
        <div
          data-testid="home-join-banner"
          className="relative z-30 text-black border-b-2 border-black/20"
          style={{ background: "linear-gradient(90deg, #F97316 0%, #FBBF24 50%, #F97316 100%)" }}
        >
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles size={14} className="shrink-0" />
              <p className="text-[11px] md:text-sm font-mono uppercase tracking-widest font-bold truncate">
                <span className="hidden sm:inline">New here?</span> Join free to book slots &amp; save on memberships.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Link
                to="/member/signup"
                data-testid="home-join-banner-cta"
                className="bg-black text-white px-3 md:px-4 py-1.5 font-bold text-[10px] md:text-xs uppercase tracking-widest inline-flex items-center gap-1.5 hover:bg-white hover:text-black transition"
              >
                Join Free <ArrowRight size={12} />
              </Link>
              <button
                onClick={dismissBanner}
                data-testid="home-join-banner-close"
                aria-label="Dismiss"
                className="text-black/60 hover:text-black p-1"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <img
          src={copy.hero_image_url || "https://images.unsplash.com/photo-1512719994953-eabf50895df7?w=2000"}
          alt="Sunset stadium"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1120] via-[#0B1120]/70 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-24 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="md:col-span-8"
          >
            <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-6">
              // Sports Complex · Academy · Rentals
            </div>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-black leading-[0.9] uppercase tracking-tighter">
              {copy.hero_title || "TRAIN LIKE A PRO. PLAY LIKE A CHAMPION."}
            </h1>
            <p className="mt-8 max-w-2xl text-lg text-white/70 leading-relaxed">
              {copy.hero_subtitle || "12 world-class facilities. 3 elite academies. One home for athletes serious about their game."}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/booking"
                data-testid="hero-book-btn"
                className="group bg-brand-blue text-white px-8 py-4 font-bold text-sm uppercase tracking-widest flex items-center gap-3 hover:glow-blue transition-all"
              >
                {copy.hero_cta || "Book Your Slot"} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/enrol"
                data-testid="hero-parent-btn"
                className="border border-white/20 text-white px-8 py-4 font-bold text-sm uppercase tracking-widest hover:border-brand-orange hover:text-brand-orange flex items-center gap-3 transition-all"
              >
                <Play size={14} /> Enrol Your Ward
              </Link>
            </div>
          </motion.div>

          <div className="md:col-span-4 space-y-4">
            {copy.hero_portrait_url && (
              <div className="relative aspect-square overflow-hidden border border-white/12 group" data-testid="hero-portrait">
                <img
                  src={copy.hero_portrait_url}
                  alt={copy.hero_portrait_name || "Head Coach"}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                {(copy.hero_portrait_name || copy.hero_portrait_role) && (
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    {copy.hero_portrait_role && (
                      <div className="font-mono text-[10px] uppercase tracking-[0.3em] brand-orange mb-1">
                        {copy.hero_portrait_role}
                      </div>
                    )}
                    {copy.hero_portrait_name && (
                      <div className="font-display text-xl font-black uppercase tracking-tight leading-none">
                        {copy.hero_portrait_name}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <StatCell label="Athletes" value={copy.stats?.athletes ?? 850} />
              <StatCell label="Coaches" value={copy.stats?.coaches ?? 24} />
              <StatCell label="Trophies" value={copy.stats?.trophies ?? 47} />
              <StatCell label="Hrs/Month" value={copy.stats?.hours_booked_month ?? 4200} />
            </div>
          </div>
        </div>
      </section>

      {/* NEWS TICKER */}
      <section className="border-y border-white/12 bg-[#131A2B] overflow-hidden" data-testid="news-ticker">
        <div className="flex items-center h-12">
          <div className="bg-brand-blue text-white font-mono text-xs uppercase tracking-widest px-5 py-3 font-bold shrink-0 flex items-center gap-2">
            <Zap size={14} /> Live
          </div>
          <div className="overflow-hidden flex-1">
            <div className="ticker-track font-mono text-sm text-white/80">
              {[...news, ...news].map((n, i) => (
                <span key={i} className="flex items-center gap-3">
                  <span className="brand-orange">▸</span> {n.headline}
                </span>
              ))}
              {news.length === 0 && <span>Loading latest updates…</span>}
            </div>
          </div>
        </div>
      </section>

      {/* FACILITIES BENTO */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24" data-testid="facilities-section">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-4">// The Twelve</div>
            <h2 className="font-display text-4xl md:text-6xl font-black uppercase leading-none tracking-tighter">
              Every facility.<br /><span className="brand-blue">Every sport.</span>
            </h2>
          </div>
          <p className="text-white/60 max-w-md hidden md:block">
            {copy.about_body || "HTP Sports Hub is a 4-acre integrated sports complex..."}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 stagger-fade">
          {services.map((s, i) => {
            const Icon = ICONS[s.icon] || Trophy;
            const span = [0, 5, 8].includes(i) ? "lg:col-span-2" : "";
            // Derive a human display price honouring the admin's Billing Unit:
            //   ball        → cheapest tier from ball_pricing matrix ("From ₹X")
            //   fixed_slot  → cheapest slot ("From ₹X / session")
            //   over        → ₹X / over
            //   others      → ₹X + unit_label
            let priceDisplay = `${formatINR(s.unit_price || 0)}${(s.unit_label || `per ${s.billing_unit || "hour"}`).replace(/^per\s+/i, "/").toUpperCase()}`;
            const bu = s.billing_unit;
            if (bu === "ball" || (Array.isArray(s.ball_pricing) && s.ball_pricing.length && !(s.unit_price > 0))) {
              const prices = (s.ball_pricing || []).flatMap((b) => (b.tiers || []).map((t) => parseFloat(t.price) || 0)).filter((p) => p > 0);
              if (prices.length) priceDisplay = `FROM ${formatINR(Math.min(...prices))}`;
            } else if (bu === "fixed_slot" || (Array.isArray(s.fixed_slots) && s.fixed_slots.length && !(s.unit_price > 0))) {
              const prices = (s.fixed_slots || []).map((f) => parseFloat(f.price) || 0).filter((p) => p > 0);
              if (prices.length) priceDisplay = `FROM ${formatINR(Math.min(...prices))} / SESSION`;
              else priceDisplay = "PRICING ON REQUEST";
            }
            const downtime = s._downtime;
            if (downtime) {
              const end = new Date(downtime.end_at);
              const endLabel = end.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
              const endHM = end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
              return (
                <div
                  key={s.slug}
                  data-testid={`facility-${s.slug}`}
                  className={`group relative border border-amber-500/40 bg-amber-950/20 aspect-square lg:aspect-auto overflow-hidden opacity-90 cursor-not-allowed ${span}`}
                  title={downtime.reason || "Facility temporarily closed for maintenance"}
                >
                  {s.cover_image && (
                    <img src={s.cover_image} alt={s.name} className="absolute inset-0 w-full h-full object-cover opacity-20 grayscale" />
                  )}
                  <div className="absolute inset-0 flex flex-col justify-between p-4 md:p-5">
                    <div className="flex items-center justify-between">
                      <div className="w-9 h-9 grid place-items-center bg-amber-500/20 border border-amber-500/50">
                        <Icon size={16} className="text-amber-400" />
                      </div>
                      <span className="text-[9px] font-mono uppercase tracking-widest bg-amber-500 text-black px-1.5 py-0.5 font-bold">
                        MAINTENANCE
                      </span>
                    </div>
                    <div>
                      <h3 className="font-display text-lg md:text-xl font-bold uppercase leading-tight tracking-tight text-white/80">{s.name}</h3>
                      <p className="text-[11px] text-amber-300/90 mt-1.5 font-mono uppercase tracking-wider">
                        Reopens {endLabel} · {endHM}
                      </p>
                      {downtime.reason && (
                        <p className="text-[10px] text-white/50 mt-1 line-clamp-2">{downtime.reason}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <Link
                key={s.slug}
                to={s.type === "hourly" ? `/booking?service=${s.slug}` : (s.slug === "gym" ? "/parent/gym" : "/enrol")}
                data-testid={`facility-${s.slug}`}
                className={`group relative border border-white/12 hover-lift aspect-square lg:aspect-auto overflow-hidden block ${span}`}
              >
                {/* Background image if configured, otherwise a subtle gradient */}
                {s.image_url ? (
                  <img
                    src={s.image_url}
                    alt={s.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-[#131A2B] via-[#0F1626] to-[#0B1120]" />
                )}
                {/* Contrast overlay so text stays readable */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />

                <div className="relative h-full p-6 flex flex-col justify-between">
                  <div className="flex items-start justify-between">
                    <div className="w-10 h-10 border border-white/30 backdrop-blur-sm bg-black/30 flex items-center justify-center group-hover:border-brand-blue group-hover:text-brand-blue group-hover:bg-brand-blue/10 transition-all">
                      <Icon size={18} />
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/60 bg-black/40 backdrop-blur-sm px-1.5 py-0.5">{s.type}</span>
                  </div>
                  <div>
                    <h3 className="font-display text-xl md:text-2xl font-bold uppercase leading-tight tracking-tight drop-shadow-lg">{s.name}</h3>
                    <p className="text-xs text-white/80 mt-2 font-mono uppercase tracking-wider drop-shadow">
                      {priceDisplay}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* SPORTS ACADEMY */}
      <section className="bg-[#131A2B] py-24 border-y border-white/12" data-testid="academy-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-4">// Elite Academies</div>
          <h2 className="font-display text-4xl md:text-6xl font-black uppercase leading-none tracking-tighter mb-12">
            Build champions.<br /><span className="brand-orange">Not just players.</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(copy.academy_cards && copy.academy_cards.length > 0 ? copy.academy_cards : [
              { slug: "cricket-academy", name: "Cricket Academy", image_url: "https://images.unsplash.com/photo-1585822754398-04873d4e1f50?w=1200", coach: "Kapil Bhatt", fee: 3500, age_groups: "U-10 · U-14 · U-19" },
              { slug: "football-academy", name: "Football Academy", image_url: "https://images.pexels.com/photos/30542672/pexels-photo-30542672.jpeg", coach: "Miguel Fernandes", fee: 3000, age_groups: "U-10 · U-14 · U-19" },
              { slug: "badminton-academy", name: "Badminton Academy", image_url: "https://images.pexels.com/photos/8796050/pexels-photo-8796050.jpeg", coach: "Priya Nair", fee: 2500, age_groups: "U-10 · U-14 · U-19" },
            ]).map((a) => (
              <div key={a.slug || a.name} data-testid={`academy-${a.slug || a.name}`} className="relative group aspect-[4/5] overflow-hidden border border-white/12">
                <img src={a.image_url} alt={a.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="font-mono text-xs uppercase tracking-widest brand-blue mb-2">{a.age_groups || "Age U-10 · U-14 · U-19"}</div>
                  <h3 className="font-display text-3xl font-black uppercase tracking-tight leading-none">{a.name}</h3>
                  <p className="text-white/70 text-sm mt-2">Head coach: {a.coach}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="font-mono text-sm brand-orange">{formatINR(a.fee)}/mo</span>
                    <Link to="/enrol" className="text-xs uppercase tracking-widest font-bold border-b border-brand-blue pb-0.5 hover:brand-blue">
                      Enrol →
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEMBERSHIP PLANS — active plans from admin */}
      {membershipPlans.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 md:px-8 py-24" data-testid="home-membership-section">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-4">// Facility Memberships</div>
              <h2 className="font-display text-4xl md:text-6xl font-black uppercase leading-none tracking-tighter">
                Play more.<br /><span className="brand-blue">Save more.</span>
              </h2>
              <p className="mt-4 max-w-xl text-white/60 text-sm md:text-base">
                Prepaid hours, overs, sessions or unlimited packs — priority booking and members-only pricing across the hub.
              </p>
            </div>
            <Link
              to="/membership"
              data-testid="home-memberships-cta"
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-brand-blue/60 text-brand-blue text-xs font-mono uppercase tracking-widest hover:bg-brand-blue hover:text-white"
            >
              See all plans →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {membershipPlans.slice(0, 6).map((plan) => {
              const TypeIcon = plan.plan_type === "unlimited" ? InfinityIcon
                : plan.plan_type === "sessions" ? Trophy
                : plan.plan_type === "overs" ? Zap
                : Clock;
              const unitLabel = plan.plan_type === "unlimited"
                ? "Unlimited access"
                : `${plan.quota || 0} ${plan.plan_type || "hrs"}`;
              const validity = plan.duration_days ? `${plan.duration_days} days` : "";
              return (
                <div
                  key={plan.id}
                  data-testid={`home-plan-${plan.id}`}
                  className="group relative border border-white/12 bg-[#131A2B] p-6 hover-lift flex flex-col"
                >
                  {plan.facility_image && (
                    <div className="absolute inset-0 opacity-[0.08]">
                      <img src={plan.facility_image} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="relative flex items-start justify-between mb-4">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] brand-orange">
                        {plan.facility_name || plan.facility_slug}
                      </div>
                      <h3 className="font-display text-2xl font-black uppercase tracking-tight leading-tight mt-1">
                        {plan.name}
                      </h3>
                    </div>
                    <div className="w-9 h-9 grid place-items-center border border-brand-blue/40 bg-brand-blue/10 text-brand-blue shrink-0">
                      <TypeIcon size={16} />
                    </div>
                  </div>

                  <div className="relative flex items-baseline gap-2 mb-4">
                    <span className="font-display text-4xl font-black brand-blue">
                      {formatINR(plan.price || 0)}
                    </span>
                    {validity && (
                      <span className="font-mono text-[11px] uppercase tracking-widest text-white/50">
                        / {validity}
                      </span>
                    )}
                  </div>

                  <ul className="relative space-y-1.5 text-sm text-white/70 mb-6 flex-1">
                    <li className="flex items-center gap-2">
                      <BadgeCheck size={14} className="brand-orange shrink-0" /> {unitLabel}
                    </li>
                    {(plan.benefits || []).slice(0, 2).map((b, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <Sparkles size={14} className="brand-orange shrink-0" /> <span className="line-clamp-1">{b}</span>
                      </li>
                    ))}
                    {plan.description && (
                      <li className="text-xs text-white/50 pt-1 line-clamp-2">{plan.description}</li>
                    )}
                  </ul>

                  <Link
                    to="/membership"
                    data-testid={`home-plan-join-${plan.id}`}
                    className="relative bg-brand-blue text-white py-3 font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:glow-blue"
                  >
                    Join Now <ArrowRight size={14} />
                  </Link>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* PRICE CALCULATOR */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24" data-testid="price-calculator">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-4">// Live Estimate</div>
            <h2 className="font-display text-4xl md:text-6xl font-black uppercase leading-none tracking-tighter">
              Know the price<br /><span className="brand-blue">before you book.</span>
            </h2>
            <p className="text-white/60 mt-6 max-w-md">
              {(org.company_type || "company") === "company" ? (
                <>All prices shown are exclusive of <span className="brand-orange">{org.gst_rate || 18}% GST</span>. Final billed amount includes tax as per Indian GST law.</>
              ) : (
                <>Every rate you see is the <span className="brand-orange">final price</span> — GST included, no surprise fees on top. What you see is what you pay.</>
              )}
            </p>
          </div>
          <div className="bg-[#131A2B] border border-white/12 p-8">
            <label className="font-mono text-xs uppercase tracking-widest text-white/40">Service</label>
            <select
              value={calcSlug}
              onChange={(e) => setCalcSlug(e.target.value)}
              data-testid="calc-service"
              className="w-full mt-2 bg-transparent border-b border-white/20 py-3 text-white font-display text-xl uppercase focus:outline-none focus:border-brand-blue"
            >
              {hourlyServices.map((s) => (
                <option key={s.slug} value={s.slug} className="bg-[#131A2B]">{s.name}</option>
              ))}
            </select>

            <label className="font-mono text-xs uppercase tracking-widest text-white/40 mt-8 block">Hours: <span className="brand-blue font-bold">{calcHours}</span></label>
            <input
              type="range" min="1" max="8" value={calcHours}
              onChange={(e) => setCalcHours(parseInt(e.target.value))}
              data-testid="calc-hours"
              className="w-full mt-3 accent-[#2563EB]"
            />

            <div className="mt-8 space-y-2 font-mono text-sm border-t border-white/12 pt-6">
              <Row label="Base" value={formatINR(calcSubtotal)} />
              {(org.company_type || "company") === "company" ? (
                <Row label={`GST @ ${org.gst_rate || 18}%`} value={formatINR(Math.round(calcSubtotal * (org.gst_rate || 18) / 100))} />
              ) : (
                <Row label="GST" value="Included" />
              )}
              <div className="flex justify-between pt-4 mt-2 border-t border-white/12">
                <span className="font-display text-2xl uppercase">Total</span>
                <span className="font-display text-3xl brand-blue font-black" data-testid="calc-total">
                  {formatINR((org.company_type || "company") === "company"
                    ? Math.round(calcSubtotal * (1 + (org.gst_rate || 18) / 100))
                    : calcTotal)}
                </span>
              </div>
            </div>
            <Link
              to={`/booking?service=${calcSlug}`}
              data-testid="calc-book-btn"
              className="mt-6 w-full bg-brand-blue text-white py-4 font-bold text-sm uppercase tracking-widest flex items-center justify-center gap-2 hover:glow-blue"
            >
              Book This Slot <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* GALLERY SLIDESHOW + AMBIENT VIDEO */}
      {(slideshowImages.length > 0 || ambientVideo) && (
        <section className="max-w-7xl mx-auto px-5 md:px-8 py-24" data-testid="gallery-showcase">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Life at the hub</div>
              <h2 className="font-display text-4xl md:text-6xl font-black uppercase leading-none tracking-tighter">
                Inside<br /><span className="brand-blue">the arena.</span>
              </h2>
            </div>
            <Link to="/gallery" className="text-xs font-mono uppercase tracking-widest brand-blue hover:underline">
              See full gallery →
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* Slideshow — spans 3/5 columns */}
            {slideshowImages.length > 0 && (
              <div className="lg:col-span-3 relative bg-black border border-white/12 overflow-hidden" data-testid="gallery-slideshow" style={{ aspectRatio: "16/10" }}>
                {slideshowImages.map((it, i) => (
                  <img
                    key={it.id}
                    src={it.image_url}
                    alt={it.title}
                    data-testid={`slide-${i}`}
                    className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
                    style={{ opacity: i === slideIdx ? 1 : 0 }}
                  />
                ))}
                {/* Bottom caption + dots */}
                <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/80 to-transparent">
                  <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">
                    {slideshowImages[slideIdx]?.category?.replace("_", " ")}
                  </div>
                  <div className="font-display text-xl font-bold text-white">
                    {slideshowImages[slideIdx]?.title}
                  </div>
                  {slideshowImages[slideIdx]?.caption && (
                    <div className="text-xs text-white/70 mt-1">{slideshowImages[slideIdx].caption}</div>
                  )}
                </div>
                <div className="absolute top-4 right-4 flex gap-1.5" data-testid="slideshow-dots">
                  {slideshowImages.slice(0, 8).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setSlideIdx(i)}
                      aria-label={`Go to slide ${i + 1}`}
                      className={`w-2 h-2 transition-all ${i === slideIdx ? "w-6 bg-brand-blue" : "bg-white/40 hover:bg-white/70"}`}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Ambient silent video — spans 2/5 columns */}
            {ambientVideo && (
              <div className="lg:col-span-2 relative bg-black border border-white/12 overflow-hidden" data-testid="ambient-video" style={{ aspectRatio: "16/10" }}>
                <video
                  key={ambientVideo.id}
                  src={ambientVideo.video_url}
                  poster={ambientVideo.image_url}
                  autoPlay muted loop playsInline
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="ambient-video-element"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30 pointer-events-none" />
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-[10px] font-mono uppercase tracking-widest text-white/80">Live · silent</span>
                </div>
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">
                    {ambientVideo.category?.replace("_", " ")}
                  </div>
                  <div className="font-display text-lg font-bold text-white">{ambientVideo.title}</div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* TESTIMONIALS — top-rated (4-5★), mix Google + direct reviews, capped at 10 */}
      <section className="bg-[#131A2B] border-y border-white/12 py-24" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-4">// Voices · Top-rated</div>
              <h2 className="font-display text-4xl md:text-6xl font-black uppercase leading-none tracking-tighter">
                Words from<br /><span className="brand-blue">the community.</span>
              </h2>
              <p className="mt-3 text-white/60 text-sm">Only 4★ and 5★ reviews — from verified Google users and our own parents.</p>
            </div>
            {copy.contact?.google_business_url && (
              <a
                href={copy.contact.google_business_url}
                target="_blank"
                rel="noreferrer"
                data-testid="home-google-cta"
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/20 bg-white text-[#0B1120] text-xs font-mono uppercase tracking-widest hover:bg-white/90"
              >
                See all on Google →
              </a>
            )}
          </div>

          {(() => {
            // Filter to 4-5 stars, split Google vs direct, interleave, cap 10.
            const highRated = testimonials.filter((t) => (t.rating || 5) >= 4);
            const google = highRated.filter((t) => (t.source || "").toLowerCase() === "google");
            const direct = highRated.filter((t) => (t.source || "").toLowerCase() !== "google");
            const merged = [];
            const maxLen = Math.max(google.length, direct.length);
            for (let i = 0; i < maxLen && merged.length < 10; i++) {
              if (google[i]) merged.push(google[i]);
              if (direct[i] && merged.length < 10) merged.push(direct[i]);
            }
            if (merged.length === 0) {
              return (
                <div className="mt-10 text-center py-16 border border-dashed border-white/15 text-white/50" data-testid="testimonials-empty">
                  No high-rated reviews yet. Ask your first happy parents to leave one!
                </div>
              );
            }
            return (
              <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="testimonials-grid">
                {merged.map((t) => {
                  const isGoogle = (t.source || "").toLowerCase() === "google";
                  return (
                    <a
                      key={t.id}
                      href={t.source_url || (isGoogle ? copy.contact?.google_business_url : undefined) || undefined}
                      target={t.source_url || isGoogle ? "_blank" : undefined}
                      rel="noreferrer"
                      data-testid={`testimonial-${t.id}`}
                      className="bg-[#0B1120] border border-white/12 p-6 hover-lift block"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-1">
                          {Array.from({ length: t.rating || 5 }).map((_, i) => (
                            <span key={i} className="text-yellow-400 text-sm">★</span>
                          ))}
                        </div>
                        {isGoogle ? (
                          <span className="inline-flex items-center gap-1 bg-white text-[#0B1120] text-[10px] px-1.5 py-0.5 font-mono uppercase tracking-widest">
                            <span style={{ color: "#4285F4" }}>G</span>
                            <span style={{ color: "#EA4335" }}>o</span>
                            <span style={{ color: "#FBBC05" }}>o</span>
                            <span style={{ color: "#4285F4" }}>g</span>
                            <span style={{ color: "#34A853" }}>l</span>
                            <span style={{ color: "#EA4335" }}>e</span>
                          </span>
                        ) : (
                          <span className="text-[10px] font-mono uppercase tracking-widest bg-brand-blue/20 text-brand-blue px-1.5 py-0.5">Parent</span>
                        )}
                      </div>
                      <p className="text-white/80 text-sm leading-relaxed line-clamp-4">&ldquo;{t.message}&rdquo;</p>
                      <div className="mt-4 flex items-center gap-2 text-xs">
                        {t.avatar_url && <img src={t.avatar_url} alt={t.name} className="w-8 h-8 rounded-full object-cover" />}
                        <div>
                          <div className="font-bold">{t.name}</div>
                          <div className="text-white/50 font-mono uppercase tracking-widest text-[10px]">{t.role}</div>
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </section>

      {/* BLOGS — latest 3 posts */}
      {blogs.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 md:px-8 py-24" data-testid="home-blogs-section">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-4">// The Journal</div>
              <h2 className="font-display text-4xl md:text-6xl font-black uppercase leading-none tracking-tighter">
                Latest from<br /><span className="brand-blue">the journal.</span>
              </h2>
            </div>
            <Link
              to="/blogs"
              data-testid="home-blogs-cta"
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-brand-blue/60 text-brand-blue text-xs font-mono uppercase tracking-widest hover:bg-brand-blue hover:text-white"
            >
              Read all posts →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {blogs.slice(0, 3).map((b) => (
              <Link
                key={b.id}
                to={`/blogs/${b.slug || b.id}`}
                data-testid={`home-blog-${b.id}`}
                className="group border border-white/12 bg-[#131A2B] overflow-hidden hover-lift block"
              >
                {b.cover_image_url && (
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={b.cover_image_url} alt={b.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  </div>
                )}
                <div className="p-5">
                  <div className="font-mono text-[10px] uppercase tracking-widest brand-orange mb-2">
                    {(b.category || "Journal")} · {b.published_at ? new Date(b.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : ""}
                  </div>
                  <h3 className="font-display text-xl uppercase font-bold tracking-tight line-clamp-2">{b.title}</h3>
                  {b.excerpt && <p className="mt-2 text-sm text-white/60 line-clamp-3">{b.excerpt}</p>}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}


      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24" data-testid="cta-section">
        <div className="relative bg-brand-blue text-white p-12 md:p-20 overflow-hidden">
          <h2 className="font-display text-4xl md:text-6xl font-black uppercase leading-none tracking-tighter max-w-3xl">
            Ready to elevate your game?
          </h2>
          <p className="mt-6 max-w-xl text-black/70">
            Book a trial session, tour our facilities, or enrol your ward into an academy batch today.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/booking" data-testid="cta-book-btn" className="bg-black text-white px-8 py-4 font-bold text-sm uppercase tracking-widest hover:bg-white/10 border border-black">
              Book a Slot
            </Link>
            <Link to="/enrol" data-testid="cta-enrol-btn" className="border border-black text-black px-8 py-4 font-bold text-sm uppercase tracking-widest hover:bg-black hover:text-white transition-all">
              Enrol Your Ward
            </Link>
            <Link to="/contact" data-testid="cta-contact-btn" className="border border-black text-black px-8 py-4 font-bold text-sm uppercase tracking-widest hover:bg-black hover:text-white transition-all">
              Contact Us
            </Link>
            {donationsOn && (
              <Link to="/donate" data-testid="cta-donate-btn" className="bg-brand-orange text-black px-8 py-4 font-bold text-sm uppercase tracking-widest hover:glow-orange flex items-center gap-2">
                <HeartHandshake size={16} /> Support the Academy
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function StatCell({ label, value }) {
  return (
    <div className="bg-[#131A2B] border border-white/12 p-4 hover-lift">
      <div className="font-display text-3xl md:text-4xl font-black brand-blue">{value}</div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mt-1">{label}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-white/70">
      <span>{label}</span>
      <span className="text-white">{value}</span>
    </div>
  );
}
